import { c as _c } from "react/compiler-runtime";
import React from 'react';
import enUS from '@commutifi-fe/ui/components/locales/en_US';
import { SupportedLocales, FrontendLocales } from '@commutifi/constants/Locale';
import { useIntl } from 'locales/index';
import { ConfigProvider as CommutifiUiConfigProvider, ConfigProviderProps as CommutifiConfigProps, Locale, NotificationConfig, MessageConfig, AntdApp } from '@commutifi-fe/ui';
import { MomentLocalization } from '../LocaleProvider';
import { useLocalStorage } from 'react-use';
import { LocalesType, STORAGE_THEME_KEY } from '@commutifi-fe/constants';
import { useThemeDetector } from '@commutifi-fe/hooks';
import { LocalConfigProvider, AppThemes, useConfig, stripePromise } from './LocalConfigProvider';
import { ChartsThemeProvider } from '@commutifi-fe/charts';
export { AppThemes, useConfig, stripePromise };
interface ConfigProviderProps {
  locale: LocalesType;
  children: React.ReactNode;
  isLoadingTranslations: boolean;
  theme?: CommutifiConfigProps['theme'];
}

// Locales that map directly to enUS — resolved synchronously (no dynamic import needed)
const syncLocales = new Set<LocalesType>([SupportedLocales.enAU, SupportedLocales.enCA, SupportedLocales.enGB, FrontendLocales.enPH, FrontendLocales.enTH, SupportedLocales.enUS, FrontendLocales.enVI]);
type AntLocaleLoader = () => Promise<{
  default: Locale;
}>;
const asyncLocaleMapping: { [K in LocalesType]?: AntLocaleLoader } = {
  [SupportedLocales.cs]: () => import(/* webpackChunkName: "antd_cs_CZ" */'@commutifi-fe/ui/components/locales/cs_CZ'),
  [SupportedLocales.deDE]: () => import(/* webpackChunkName: "antd_de_DE" */'@commutifi-fe/ui/components/locales/de_DE'),
  [SupportedLocales.esES]: () => import(/* webpackChunkName: "antd_es_ES" */'@commutifi-fe/ui/components/locales/es_ES'),
  [FrontendLocales.esCL]: () => import(/* webpackChunkName: "antd_es_CL" */'@commutifi-fe/ui/components/locales/es_ES'),
  [FrontendLocales.esCO]: () => import(/* webpackChunkName: "antd_es_CO" */'@commutifi-fe/ui/components/locales/es_ES'),
  [FrontendLocales.esMX]: () => import(/* webpackChunkName: "antd_es_MX" */'@commutifi-fe/ui/components/locales/es_ES'),
  [FrontendLocales.esUS]: () => import(/* webpackChunkName: "antd_es_US" */'@commutifi-fe/ui/components/locales/es_ES'),
  [SupportedLocales.frBE]: () => import(/* webpackChunkName: "antd_fr_BE" */'@commutifi-fe/ui/components/locales/fr_BE'),
  [SupportedLocales.frCA]: () => import(/* webpackChunkName: "antd_fr_CA" */'@commutifi-fe/ui/components/locales/fr_CA'),
  [SupportedLocales.frFR]: () => import(/* webpackChunkName: "antd_fr_FR" */'@commutifi-fe/ui/components/locales/fr_FR'),
  [SupportedLocales.itIT]: () => import(/* webpackChunkName: "antd_it_IT" */'@commutifi-fe/ui/components/locales/it_IT'),
  [SupportedLocales.nlBE]: () => import(/* webpackChunkName: "antd_nl_BE" */'@commutifi-fe/ui/components/locales/nl_BE'),
  [SupportedLocales.nlNL]: () => import(/* webpackChunkName: "antd_nl_NL" */'@commutifi-fe/ui/components/locales/nl_NL'),
  [SupportedLocales.pl]: () => import(/* webpackChunkName: "antd_pl_PL" */'@commutifi-fe/ui/components/locales/pl_PL'),
  [SupportedLocales.ptPT]: () => import(/* webpackChunkName: "antd_pt_PT" */'@commutifi-fe/ui/components/locales/pt_PT')
};
const getInitialLocale = (locale: LocalesType): Locale | undefined => syncLocales.has(locale) ? enUS : undefined;
const setMobileThemeBar = (isDark: boolean) => {
  const color = isDark ? '#000000' : '#ffffff';
  // Mobile theme color
  document.querySelector("meta[name='theme-color']")?.setAttribute('content', color);
};
const antdAppStyle = {
  minHeight: '100%',
  display: 'flex',
  flex: 1
};
const notificationConfig: NotificationConfig = {
  bottom: 10
};
const messageConfig: MessageConfig = {
  duration: 6,
  maxCount: 3
};

/**
 * Setup Antd ConfigProvider and Commutifi UI components ConfigProvider to have a consistent design system.
 * Also setup the local (code of this project specifically) ConfigProvider component to propagate some general configuration
 * about Stripe, antd phone input, etc.
 *
 * Note: changeTheme is passed down to simplify re-rendering but we could setup a useState in the LocalConfigProvider
 *       to trigger re-renders so stripe options and more are applied correctly.
 */
export function UiComponentsConfigProvider(t0) {
  const $ = _c(37);
  const {
    children,
    locale,
    isLoadingTranslations,
    theme
  } = t0;
  const detectedTheme = useThemeDetector();
  const [currentTheme, setCurrentTheme] = useLocalStorage(STORAGE_THEME_KEY, AppThemes.LIGHT);
  let t1;
  if ($[0] !== detectedTheme || $[1] !== setCurrentTheme) {
    t1 = theme_0 => {
      setCurrentTheme(theme_0);
      const processedTheme = theme_0 === AppThemes.SYSTEM ? detectedTheme === "dark" ? AppThemes.DARK : AppThemes.LIGHT : theme_0;
      bb10: switch (processedTheme) {
        case "dark":
        case AppThemes.DARK:
          {
            setMobileThemeBar(true);
            break bb10;
          }
        case "light":
        case AppThemes.LIGHT:
        default:
          {
            setMobileThemeBar(false);
          }
      }
    };
    $[0] = detectedTheme;
    $[1] = setCurrentTheme;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const changeTheme = t1;
  const intl = useIntl();
  let t2;
  if ($[3] !== intl) {
    t2 = intl.formatMessage({
      id: "required"
    });
    $[3] = intl;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = {
      required: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const validateMessages = t3;
  let t4;
  if ($[7] !== locale) {
    t4 = () => getInitialLocale(locale);
    $[7] = locale;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const [antdLocale, setAntdLocale] = React.useState(t4);
  let t5;
  let t6;
  if ($[9] !== locale) {
    t5 = () => {
      if (syncLocales.has(locale)) {
        setAntdLocale(enUS);
        return;
      }
      asyncLocaleMapping[locale]?.().then(module => {
        setAntdLocale(module.default);
      }).catch(_temp);
    };
    t6 = [locale];
    $[9] = locale;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  React.useEffect(t5, t6);
  const t7 = theme?.appearance || (currentTheme === AppThemes.DARK || currentTheme === AppThemes.SYSTEM && detectedTheme === AppThemes.DARK ? AppThemes.DARK : AppThemes.LIGHT);
  let t8;
  if ($[12] !== t7 || $[13] !== theme) {
    t8 = {
      ...theme,
      appearance: t7
    };
    $[12] = t7;
    $[13] = theme;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== validateMessages) {
    t9 = {
      validateMessages
    };
    $[15] = validateMessages;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== antdLocale || $[18] !== t8 || $[19] !== t9) {
    t10 = {
      theme: t8,
      locale: antdLocale,
      form: t9
    };
    $[17] = antdLocale;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const antdConfig = t10;
  const t11 = currentTheme === AppThemes.DARK ? AppThemes.DARK : AppThemes.LIGHT;
  let t12;
  if ($[21] !== children || $[22] !== t11) {
    t12 = <ChartsThemeProvider colorScheme={t11}>{children}</ChartsThemeProvider>;
    $[21] = children;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== changeTheme || $[25] !== currentTheme || $[26] !== isLoadingTranslations || $[27] !== locale || $[28] !== t12) {
    t13 = <AntdApp style={antdAppStyle} notification={notificationConfig} message={messageConfig}><LocalConfigProvider locale={locale} isLoadingTranslations={isLoadingTranslations} changeTheme={changeTheme} currentTheme={currentTheme}>{t12}</LocalConfigProvider></AntdApp>;
    $[24] = changeTheme;
    $[25] = currentTheme;
    $[26] = isLoadingTranslations;
    $[27] = locale;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== locale) {
    t14 = <MomentLocalization locale={locale} />;
    $[30] = locale;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== antdConfig || $[33] !== locale || $[34] !== t13 || $[35] !== t14) {
    t15 = <CommutifiUiConfigProvider {...antdConfig} localeKey={locale}>{t13}{t14}</CommutifiUiConfigProvider>;
    $[32] = antdConfig;
    $[33] = locale;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
}
function _temp() {}